'use client';

// Third Party
import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import classNames from 'classnames';
import Link from 'next/link';
import * as Sentry from '@sentry/nextjs';
import { signOut, useSession } from '@lib/auth/client';
import { useQuery } from '@apollo/client/react';

// Utils
import { routes } from '@routes';
import { getReferralCode, removeReferralCode } from '@utils/referral-code';
import { STEP_LABELS, STEP_ROUTES, isValidOnboardingStepKey } from '@utils/onboarding-steps';
import { useMutation } from '@apollo/client/react';
import { UserAddReferralCodeDocument, AccountForCurrentUserDocument, PmOnboardingSessionStatus } from '@gql';

// UI
import { AnalyticsRef } from '@components/analytics-ref';
import { Avatar } from '@components/avatar';
import { Icon, IconNames } from '@components/icon';
interface NavigationItem {
  name: string;
  href: VoidFunction | string;
  newWindow?: boolean;
  tagRef?: string;
}

/**
 * Renders a navigation item based on its type (link, button, or static)
 */
function renderNavigationItem(name: string, active: boolean, href?: VoidFunction | string, newWindow?: boolean) {
  const baseClasses = classNames(active ? 'bg-gray-xlight' : '', 'block py-3 px-6 text-sm text-primary-main');
  if (typeof href === 'string') {
    const isExternal = href.startsWith('http') || newWindow;
    if (isExternal) {
      return <a href={href} className={baseClasses} target="_blank" rel="noopener noreferrer">
          {name}
        </a>;
    }
    const isAuthModal = href === '/login' || href.startsWith('/signup');
    return <Link href={href} scroll={!isAuthModal} className={baseClasses}>
        {name}
      </Link>;
  }
  if (typeof href === 'function') {
    return <button type="button" className={classNames(baseClasses, 'w-full text-start', 'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-light')} onClick={() => href()}>
        {name}
      </button>;
  }
  return <span className={baseClasses}>{name}</span>;
}

/**
 * Helper to get the properties link text and route based on onboarding status
 */
function getPropertiesLinkInfo(pmOnboardingSession?: {
  status: PmOnboardingSessionStatus;
  currentStepKey: string;
} | null): {
  name: string;
  href: string;
} {
  if (pmOnboardingSession?.status === PmOnboardingSessionStatus.InProgress && pmOnboardingSession.currentStepKey && isValidOnboardingStepKey(pmOnboardingSession.currentStepKey)) {
    const stepKey = pmOnboardingSession.currentStepKey;
    const stepLabel = STEP_LABELS[stepKey];
    return {
      name: `Continue: ${stepLabel}`,
      href: STEP_ROUTES[stepKey]
    };
  }
  return {
    name: 'Manage your properties',
    href: routes.accountListings
  };
}
export const UserMenu: React.FC = () => {
  const $ = _c(61);
  const {
    data: session
  } = useSession();
  const [addReferralCode] = useMutation(UserAddReferralCodeDocument);
  const [isMounted, setIsMounted] = React.useState(false);
  const isHost = session?.user?.isHost;
  const t0 = !session?.user || !isHost;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      skip: t0,
      fetchPolicy: "cache-first"
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: accountData
  } = useQuery(AccountForCurrentUserDocument, t1);
  const account = accountData?.accountForCurrentUser?.__typename === "Account" ? accountData.accountForCurrentUser : null;
  const pmOnboardingSession = account?.pmOnboardingSession;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsMounted(true);
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  React.useEffect(t2, t3);
  let t4;
  if ($[4] !== addReferralCode || $[5] !== session?.user) {
    t4 = () => {
      if (!session?.user) {
        return;
      }
      const referralCode = getReferralCode(undefined);
      if (referralCode) {
        addReferralCode({
          variables: {
            referralCode
          }
        }).then(_temp).catch(_temp2);
      }
    };
    $[4] = addReferralCode;
    $[5] = session?.user;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== addReferralCode || $[8] !== session) {
    t5 = [session, addReferralCode];
    $[7] = addReferralCode;
    $[8] = session;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  React.useEffect(t4, t5);
  let T0;
  let T1;
  let T2;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[10] !== isHost || $[11] !== isMounted || $[12] !== pmOnboardingSession || $[13] !== session?.user) {
    t18 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const propertiesLinkInfo = isHost ? getPropertiesLinkInfo(pmOnboardingSession) : null;
      const userNavigationSections = [[{
        name: "Favorites",
        href: session?.user ? routes.userAccountFavorites : routes.login
      }, {
        name: "Refer & Earn",
        href: routes.referAndEarn,
        tagRef: "referral_nav_cta"
      }, ...(session?.user ? [{
        name: "Trips",
        href: routes.trips
      }, ...(propertiesLinkInfo ? [{
        name: propertiesLinkInfo.name,
        href: propertiesLinkInfo.href
      }] : []), {
        name: "Account",
        href: routes.userAccountSettings
      }] : [{
        name: "Traveler Signup",
        href: routes.signup()
      }])], [{
        name: "List your Properties",
        href: routes.list
      }, {
        name: "Help",
        href: routes.help,
        newWindow: true
      }, ...(session?.user ? [{
        name: "Log out",
        href: _temp3
      }] : [{
        name: "Log In",
        href: routes.login
      }])]];
      let t19;
      if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
        t19 = <Icon name={IconNames.BARS} className="mr-4" />;
        $[30] = t19;
      } else {
        t19 = $[30];
      }
      const t20 = session?.user?.firstName;
      const t21 = session?.user?.lastName;
      const t22 = session?.user?.avatarUrl;
      let t23;
      if ($[31] !== t20 || $[32] !== t21 || $[33] !== t22) {
        t23 = <div className="flex items-center rounded-full border-[1.5px] border-primary-main py-1 pl-4 pr-1">{t19}<div className="h-8 w-8"><Avatar firstName={t20} lastName={t21} avatarUrl={t22} shape="circle" /></div></div>;
        $[31] = t20;
        $[32] = t21;
        $[33] = t22;
        $[34] = t23;
      } else {
        t23 = $[34];
      }
      const menuButtonContent = t23;
      if (!isMounted) {
        let t24;
        if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
          t24 = <span className="sr-only">Open user menu</span>;
          $[35] = t24;
        } else {
          t24 = $[35];
        }
        let t25;
        if ($[36] !== menuButtonContent) {
          t25 = <div className="relative z-50 ml-5 flex-shrink-0"><div><button type="button" className="flex items-center rounded-full bg-white hover:shadow-lg" aria-haspopup="menu" aria-expanded={false}>{t24}{menuButtonContent}</button></div></div>;
          $[36] = menuButtonContent;
          $[37] = t25;
        } else {
          t25 = $[37];
        }
        t18 = t25;
        break bb0;
      }
      T2 = Menu;
      t15 = "div";
      t16 = "relative z-50 ml-5 flex-shrink-0";
      let t24;
      if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
        t24 = <span className="sr-only">Open user menu</span>;
        $[38] = t24;
      } else {
        t24 = $[38];
      }
      if ($[39] !== menuButtonContent) {
        t17 = <div><MenuButton className="flex items-center rounded-full bg-white hover:shadow-lg">{t24}{menuButtonContent}</MenuButton></div>;
        $[39] = menuButtonContent;
        $[40] = t17;
      } else {
        t17 = $[40];
      }
      T1 = Transition;
      t8 = "div";
      t9 = "transition ease-out duration-100";
      t10 = "transform opacity-0 scale-95";
      t11 = "transform opacity-100 scale-100";
      t12 = "transition ease-in duration-75";
      t13 = "transform opacity-100 scale-100";
      t14 = "transform opacity-0 scale-95";
      T0 = MenuItems;
      t6 = "absolute right-0 mt-2 w-64 origin-top-right divide-y divide-primary-main rounded-md border-[1.5px] border-primary-main bg-white shadow-lg focus:outline-none";
      t7 = userNavigationSections.map(_temp5);
    }
    $[10] = isHost;
    $[11] = isMounted;
    $[12] = pmOnboardingSession;
    $[13] = session?.user;
    $[14] = T0;
    $[15] = T1;
    $[16] = T2;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
    $[22] = t15;
    $[23] = t16;
    $[24] = t17;
    $[25] = t18;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    T0 = $[14];
    T1 = $[15];
    T2 = $[16];
    t10 = $[17];
    t11 = $[18];
    t12 = $[19];
    t13 = $[20];
    t14 = $[21];
    t15 = $[22];
    t16 = $[23];
    t17 = $[24];
    t18 = $[25];
    t6 = $[26];
    t7 = $[27];
    t8 = $[28];
    t9 = $[29];
  }
  if (t18 !== Symbol.for("react.early_return_sentinel")) {
    return t18;
  }
  let t19;
  if ($[41] !== T0 || $[42] !== t6 || $[43] !== t7) {
    t19 = <T0 className={t6}>{t7}</T0>;
    $[41] = T0;
    $[42] = t6;
    $[43] = t7;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== T1 || $[46] !== t10 || $[47] !== t11 || $[48] !== t12 || $[49] !== t13 || $[50] !== t14 || $[51] !== t19 || $[52] !== t8 || $[53] !== t9) {
    t20 = <T1 as={t8} enter={t9} enterFrom={t10} enterTo={t11} leave={t12} leaveFrom={t13} leaveTo={t14}>{t19}</T1>;
    $[45] = T1;
    $[46] = t10;
    $[47] = t11;
    $[48] = t12;
    $[49] = t13;
    $[50] = t14;
    $[51] = t19;
    $[52] = t8;
    $[53] = t9;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== T2 || $[56] !== t15 || $[57] !== t16 || $[58] !== t17 || $[59] !== t20) {
    t21 = <T2 as={t15} className={t16}>{t17}{t20}</T2>;
    $[55] = T2;
    $[56] = t15;
    $[57] = t16;
    $[58] = t17;
    $[59] = t20;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  return t21;
};
function _temp(result) {
  if (!result.error && result.data?.userAddReferralCode) {
    removeReferralCode(undefined);
  }
}
function _temp2(error) {
  Sentry.captureException(error);
}
function _temp3() {
  return signOut({
    callbackUrl: "/",
    redirect: true
  });
}
function _temp4(item) {
  return <MenuItem key={item.name}>{t0 => {
      const {
        active
      } = t0;
      const navigationItem = renderNavigationItem(item.name, active, item.href, item.newWindow);
      if (item.tagRef) {
        return <AnalyticsRef name={item.tagRef}>{navigationItem}</AnalyticsRef>;
      }
      return navigationItem;
    }}</MenuItem>;
}
function _temp5(section, index) {
  return <div key={index} className="py-2">{section.map(_temp4)}</div>;
}